import { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import useGa4Property from "../hooks/useGa4Property"
import { supabase } from "../supabaseClient"
import { useOrganization } from "../OrganizationContext"
import { useUser } from "../UserContext"
import {
  createCrossDomainCoverageEvaluator,
  formatCrossDomainActionMessage,
  loadFlaggedHostnameOwnerPairs,
} from "../utils/ga4CrossDomain"

export default function GA4HostnameAudit() {
  const navigate = useNavigate()
  const { search } = useLocation()
  const propertyId = new URLSearchParams(search).get("id")
  const user = useUser()
  const { selectedOrgId } = useOrganization()
  const [hostnames, setHostnames] = useState([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditError, setAuditError] = useState(null)
  const [noteEdits, setNoteEdits] = useState({})
  const [notesSaving, setNotesSaving] = useState({})
  const [noteErrors, setNoteErrors] = useState({})
  const [settingsErrors, setSettingsErrors] = useState([])
  const [settingsLoading, setSettingsLoading] = useState(false)
  const [settingsError, setSettingsError] = useState(null)
  const [coverageErrors, setCoverageErrors] = useState([])
  const [coverageError, setCoverageError] = useState(null)
  const [flaggedHostnameOwnerPairs, setFlaggedHostnameOwnerPairs] = useState(null)
  const {
    property: propInfo,
    loading: propertyLoading,
    error: propertyError,
  } = useGa4Property(propertyId)
  const crossDomainEvaluator = useMemo(
    () =>
      createCrossDomainCoverageEvaluator({
        supabaseClient: supabase,
        selectedOrgId,
        userId: user?.id || null,
      }),
    [selectedOrgId, user?.id],
  )

  useEffect(() => {
    setSettingsErrors([])
    setSettingsError(null)
    setSettingsLoading(false)
    setCoverageErrors([])
    setCoverageError(null)
    setFlaggedHostnameOwnerPairs(null)
  }, [propertyId])

  useEffect(() => {
    if (propertyLoading) return

    if (propertyError || !propInfo?.id) {
      setHostnames([])
      setNoteEdits({})
      setNotesSaving({})
      setNoteErrors({})
      setAuditError(propertyError || null)
      setAuditLoading(false)
      setSettingsErrors([])
      setSettingsLoading(false)
      setSettingsError(propertyError || null)
      return
    }

    let cancelled = false

    const loadAudit = async () => {
      setAuditLoading(true)
      setAuditError(null)
      try {
        const { data: rows, error } = await supabase
          .from("ga4_hostname_audit_results")
          .select(
            "id, hostname, first_detected, last_detected, session_count, expected, notes, event_owners"
          )
          .eq("ga4_property_id", propInfo.id)
        if (error) throw error
        if (cancelled) return
        setHostnames(rows || [])
        setNoteEdits({})
        setNotesSaving({})
        setNoteErrors({})
      } catch (err) {
        if (cancelled) return
        console.error("Failed to load GA4 hostname audit", err)
        setHostnames([])
        setNoteEdits({})
        setNotesSaving({})
        setNoteErrors({})
        setAuditError(err)
      } finally {
        if (!cancelled) setAuditLoading(false)
      }
    }

    const loadSettingsErrors = async () => {
      setSettingsLoading(true)
      setSettingsError(null)
      try {
        const { data, error } = await supabase
          .from("ga4_property_settings")
          .select("settings, created_at")
          .eq("ga4_property_id", propInfo.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle()
        if (error) throw error
        if (cancelled) return
        const rawErrors = Array.isArray(data?.settings?.errors)
          ? data.settings.errors
          : []
        const filtered = rawErrors
          .map((value) =>
            typeof value === "string" && value.trim() ? value.trim() : null,
          )
          .filter((value) => value !== null)
        setSettingsErrors(filtered)
      } catch (err) {
        if (cancelled) return
        console.error("Failed to load GA4 settings errors", err)
        setSettingsErrors([])
        setSettingsError(err)
      } finally {
        if (!cancelled) setSettingsLoading(false)
      }
    }

    loadAudit()
    loadSettingsErrors()

    return () => {
      cancelled = true
    }
  }, [propInfo?.id, propertyLoading, propertyError])

  useEffect(() => {
    let cancelled = false
    const loadFlags = async () => {
      const result = await loadFlaggedHostnameOwnerPairs(supabase)
      if (cancelled) return
      setFlaggedHostnameOwnerPairs({
        pairSet: new Set(result.pairSet || []),
        hostSet: new Set(result.hostSet || []),
      })
    }
    loadFlags()
    return () => {
      cancelled = true
    }
  }, [propertyId])

  const updateExpected = async (id, value) => {
    const previousHostnames = hostnames
    const previousEntry = previousHostnames.find((h) => h.id === id)
    const nextHostnames = hostnames.map((h) =>
      h.id === id ? { ...h, expected: value } : h,
    )

    if (!previousEntry) return

    const previousExpected = Boolean(previousEntry.expected)
    const nextExpected = Boolean(value)

    if (previousExpected === nextExpected) return

    setHostnames(nextHostnames)
    recomputeCoverageErrors(nextHostnames, flaggedHostnameOwnerPairs)

    try {
      const { error } = await supabase
        .from("ga4_hostname_audit_results")
        .update({ expected: value })
        .eq("id", id)
      if (error) throw error
    } catch (err) {
      console.error("Failed to update expected flag", err)
      setHostnames(previousHostnames)
      recomputeCoverageErrors(previousHostnames, flaggedHostnameOwnerPairs)
    }
  }

  const recomputeCoverageErrors = useCallback(
    async (entries, overrideFlaggedPairs = null, options = {}) => {
      const signal = options?.signal || null
      if (!crossDomainEvaluator || !propInfo?.id) {
        if (!signal?.aborted) {
          setCoverageErrors([])
          setCoverageError(null)
        }
        return
      }
      if (!Array.isArray(entries) || entries.length === 0) {
        if (!signal?.aborted) {
          setCoverageErrors([])
          setCoverageError(null)
        }
        return
      }
      try {
        if (!signal?.aborted) setCoverageError(null)
        const result = await crossDomainEvaluator({
          propertyRecord: propInfo,
          accountId: propInfo?.account_id || null,
          hostnames: entries,
          normalizedMeasurementIds: [],
          flaggedPairs: overrideFlaggedPairs || flaggedHostnameOwnerPairs,
        })
        const missingHosts = result?.missingHosts || []
        if (!signal?.aborted) {
          setCoverageErrors(
            missingHosts.map((host) => formatCrossDomainActionMessage(host)),
          )
        }
      } catch (err) {
        console.error("Failed to evaluate cross-domain coverage", err)
        if (!signal?.aborted) {
          setCoverageErrors([])
          setCoverageError(err)
        }
      }
    },
    [crossDomainEvaluator, flaggedHostnameOwnerPairs, propInfo],
  )

  useEffect(() => {
    if (!propInfo?.id || propertyLoading) {
      return
    }
    const abortRef = { aborted: false }
    recomputeCoverageErrors(hostnames, flaggedHostnameOwnerPairs, {
      signal: abortRef,
    })
    return () => {
      abortRef.aborted = true
    }
  }, [
    flaggedHostnameOwnerPairs,
    hostnames,
    propInfo?.id,
    propertyLoading,
    recomputeCoverageErrors,
  ])

  const combinedActionErrors = useMemo(() => {
    const seen = new Set()
    const merged = []
    const append = (values) => {
      for (const value of values || []) {
        if (typeof value !== "string") continue
        const trimmed = value.trim()
        if (!trimmed) continue
        if (seen.has(trimmed)) continue
        seen.add(trimmed)
        merged.push(trimmed)
      }
    }
    append(settingsErrors)
    append(coverageErrors)
    return merged
  }, [coverageErrors, settingsErrors])

  const persistNotes = async (id, value) => {
    setNotesSaving((prev) => ({ ...prev, [id]: true }))
    setNoteErrors((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
    try {
      const { error } = await supabase
        .from("ga4_hostname_audit_results")
        .update({ notes: value })
        .eq("id", id)
      if (error) throw error
      setHostnames((prev) =>
        prev.map((h) => (h.id === id ? { ...h, notes: value } : h))
      )
      setNoteEdits((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
    } catch (err) {
      console.error("Failed to update hostname notes", err)
      const message = err?.message || "Failed to update notes"
      setNoteErrors((prev) => ({ ...prev, [id]: message }))
    } finally {
      setNotesSaving((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
    }
  }

  const handleNoteBlur = (id) => {
    const host = hostnames.find((h) => h.id === id)
    if (!host) return
    const currentValue = host.notes || ""
    const nextValue =
      Object.prototype.hasOwnProperty.call(noteEdits, id)
        ? noteEdits[id] ?? ""
        : currentValue
    if (nextValue === currentValue) {
      setNoteEdits((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, id)) return prev
        const copy = { ...prev }
        delete copy[id]
        return copy
      })
      return
    }
    persistNotes(id, nextValue)
  }

  const handleViewDetails = (hostnameId) => {
    navigate(
      `/ga4-property/hostname-audit/view?id=${encodeURIComponent(
        propertyId
      )}&hostnameId=${encodeURIComponent(hostnameId)}`
    )
  }

  const renderNotesCell = (row) => {
    const value = Object.prototype.hasOwnProperty.call(noteEdits, row.id)
      ? noteEdits[row.id] ?? ""
      : row.notes || ""
    const saving = Boolean(notesSaving[row.id])
    const error = noteErrors[row.id]
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
        <textarea
          value={value}
          onChange={(e) =>
            setNoteEdits((prev) => ({ ...prev, [row.id]: e.target.value }))
          }
          onBlur={() => handleNoteBlur(row.id)}
          placeholder="Add notes"
          style={{ width: "100%", minHeight: "3.5rem" }}
          disabled={saving}
        />
        {(saving || error) && (
          <div
            style={{
              fontSize: "0.85rem",
              color: saving ? "#555" : "#b00020",
            }}
          >
            {saving ? "Saving…" : error}
          </div>
        )}
      </div>
    )
  }

  const formatEventOwners = (owners) => {
    if (!Array.isArray(owners)) return "—"
    const filtered = owners
      .filter((owner) => typeof owner === "string")
      .map((owner) => owner.trim())
      .filter((owner) => owner && owner !== "(not set)")
    if (!filtered.length) return "—"
    return filtered.join(", ")
  }

  const current = hostnames.filter((h) => !h.last_detected)
  const historic = hostnames.filter((h) => h.last_detected)

  return (
    <div className="page-container">
      <h2>Hostname Audit</h2>
      <button onClick={() => navigate(`/ga4-property?id=${encodeURIComponent(propertyId)}`)}>
        Back
      </button>
      {propertyLoading || auditLoading || settingsLoading ? (
        <p>Loading...</p>
      ) : propertyError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load property details.
        </p>
      ) : auditError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load hostname audit data.
        </p>
      ) : settingsError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load actionable errors for this property.
        </p>
      ) : (
        <>
          {propInfo ? (
            <div style={{ marginTop: "1rem" }}>
              <h3 style={{ margin: 0 }}>
                {propInfo.property_name}
                <span
                  style={{
                    fontWeight: "normal",
                    fontSize: "0.9em",
                    marginLeft: "0.5rem",
                  }}
                >
                  ({propInfo.property_id})
                </span>
              </h3>
            </div>
          ) : (
            <p style={{ marginTop: "1rem" }}>Property not found.</p>
          )}
          {combinedActionErrors.length > 0 && (
            <div
              style={{
                marginTop: "1rem",
                padding: "1rem 1.25rem",
                borderRadius: "0.75rem",
                border: "1px solid #b91c1c",
                backgroundColor: "#fef2f2",
                color: "#7f1d1d",
              }}
            >
              <h4 style={{ margin: "0 0 0.5rem 0" }}>Action Required</h4>
              <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                {combinedActionErrors.map((message, index) => (
                  <li key={`${message}-${index}`}>{message}</li>
                ))}
              </ul>
            </div>
          )}
          {coverageError && (
            <p style={{ marginTop: "0.75rem", color: "#b00020" }}>
              Failed to evaluate cross-domain coverage for the selected hostnames.
            </p>
          )}
          {current.length ? (
            <table className="ui-table"
              border="1"
              cellPadding="8"
              style={{ width: "100%", marginTop: "1rem" }}
            >
              <thead>
                <tr>
                  <th>Hostname</th>
                  <th>First Detected</th>
                  <th>Sessions</th>
                  <th>Event Owners</th>
                  <th>Expected?</th>
                  <th style={{ width: "30%" }}>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {current.map((h) => (
                  <tr key={h.id}>
                    <td>{h.hostname}</td>
                    <td>{h.first_detected}</td>
                    <td>{h.session_count}</td>
                    <td>{formatEventOwners(h.event_owners)}</td>
                    <td
                      onClick={() => updateExpected(h.id, !h.expected)}
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        checked={h.expected}
                        onChange={(e) => updateExpected(h.id, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Mark ${h.hostname} as ${h.expected ? "unexpected" : "expected"}`}
                      />
                    </td>
                    <td>{renderNotesCell(h)}</td>
                    <td>
                      <button onClick={() => handleViewDetails(h.id)}>
                        View more
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ marginTop: "1rem" }}>No current hostnames</p>
          )}
          {historic.length > 0 && (
            <>
              <h4 style={{ marginTop: "1.5rem" }}>Historic Hostnames</h4>
              <table className="ui-table"
                border="1"
                cellPadding="8"
                style={{ width: "100%", marginTop: "0.5rem" }}
              >
                <thead>
                  <tr>
                    <th>Hostname</th>
                    <th>First Detected</th>
                    <th>Last Detected</th>
                    <th>Event Owners</th>
                    <th>Expected?</th>
                    <th style={{ width: "30%" }}>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {historic.map((h) => (
                    <tr key={h.id}>
                      <td>{h.hostname}</td>
                      <td>{h.first_detected}</td>
                      <td>{h.last_detected}</td>
                      <td>{formatEventOwners(h.event_owners)}</td>
                      <td
                        onClick={() => updateExpected(h.id, !h.expected)}
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          type="checkbox"
                          checked={h.expected}
                          onChange={(e) => updateExpected(h.id, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Mark ${h.hostname} as ${h.expected ? "unexpected" : "expected"}`}
                        />
                      </td>
                      <td>{renderNotesCell(h)}</td>
                      <td>
                        <button onClick={() => handleViewDetails(h.id)}>
                          View more
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </div>
  )
}
